import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { Box, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { userHasPermission } from "@accredo/fe-shared/utils/policyUtils.js";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { usePaymentMatchingData } from "../../hooks/usePaymentMatchingData.js";
import { SEARCH_WORD_KEY } from "../../components/tables/utils/tableUtils.js";
import { DeleteBookingModal } from "./components/DeleteBookingModal.jsx";
import ClaimInfoModal from "./components/ClaimInfoModal.jsx";
import BookingExportInfoModal from "./components/BookingExportInfoModal.jsx";
import OverpaymentDialog from "./components/OverpaymentDialog.jsx";
import ConfirmNonExactMatchDialog from "./components/ConfirmNonExactMatchDialog.jsx";
import AssignExportModal from "../admin/bookings/components/AssignExportModal.jsx";
import PaymentMatchingClaimList from "./components/PaymentMatchingClaimList.jsx";
import PaymentMatchingClaimDetailsPanel from "./components/PaymentMatchingClaimDetailsPanel.jsx";
import PaymentMatchingPaymentsPanel from "./components/PaymentMatchingPaymentsPanel.jsx";

export default function PaymentMatchingPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { setBreadcrumbs } = useBreadcrumb();
  const { user } = useUser();
  const canViewBookingExport = userHasPermission(user, "booking_export.view");

  const {
    paymentImport,
    payments,
    paymentMatchings,
    bookings,
    bookingIn,
    bookingOut,
    differenceAmount,
    loadingPayments,
    loadingMatchings,
    fetchAllInitialData,
    fetchPayments,
    fetchBookings,
    deleteBooking,
    assignPaymentsToClaim,
    approveAllExactMatches,
    ignorePayment,
    ignoreRemainingPayments,
  } = usePaymentMatchingData(id);

  // paginierte bzw gefilterte claims.
  const [claims, setClaims] = useState([]);
  // Claims die eine mögliche bzw. exakte übereinstimmung haben.
  const [matchedClaims, setMatchedClaims] = useState([]);

  const [claimsSearchword, setclaimsSearchword] = useState(
    searchParams.get("search") || ""
  );
  const [paymentsSearchword, setPaymentsSearchword] = useState(
    searchParams.get("payment_search") || ""
  );

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [selectedPayments, setSelectedPayments] = useState([]);

  const [loadingClaims, setLoadingClaims] = useState(false);
  const [loadingMoreClaims, setLoadingMoreClaims] = useState(false);
  const [claimsPage, setClaimsPage] = useState(1);
  const [hasMoreClaims, setHasMoreClaims] = useState(true);
  const claimsListRef = useRef(null);

  const [isAssigning, setIsAssigning] = useState(false);
  const [isDeletingBooking, setIsDeletingBooking] = useState(false);

  const [selectedBookingToDelete, setSelectedBookingToDelete] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuBookingId, setMenuBookingId] = useState(null);

  const [paymentFilter, setPaymentFilter] = useState("all");

  // Overpayment dialog state
  const [isOverpaymentDialogOpen, setIsOverpaymentDialogOpen] = useState(false);
  const [overpaymentAmount, setOverpaymentAmount] = useState(0);

  // Sicherheitsabfrage, wenn die gewählte Zahlung kein exaktes Matching hat
  const [isNonExactMatchDialogOpen, setIsNonExactMatchDialogOpen] = useState(false);

  // Claim details dialog state
  const [isClaimDialogOpen, setIsClaimDialogOpen] = useState(false);
  const [detailedClaim, setDetailedClaim] = useState(null);
  const [detailedClaimBookings, setDetailedClaimBookings] = useState([]);
  const [loadingDetailedClaim, setLoadingDetailedClaim] = useState(false);

  // Booking-Export-Detail dialog state (für per Export verarbeitete Auszahlungen)
  const [isBookingExportDialogOpen, setIsBookingExportDialogOpen] = useState(false);
  const [detailedBookingExport, setDetailedBookingExport] = useState(null);
  const [loadingBookingExport, setLoadingBookingExport] = useState(false);

  // Nicht zugeordnete Auszahlung, die einem Booking-Export zugeordnet werden soll
  const [paymentToAssignToExport, setPaymentToAssignToExport] = useState(null);

  const filteredPayments = useMemo(
    () =>
      payments.filter((payment) => {
        // Filter by assigned/unassigned status - eine Zahlung gilt auch als
        // zugeordnet, wenn sie einem Booking-Export zugewiesen wurde (Auszahlungen,
        // die automatisch beim Import gegen bestehende Buchungen verrechnet wurden).
        const isPaymentAssigned =
          Boolean(payment.booking_id) || Boolean(payment.booking_export_id);
        if (paymentFilter === "assigned" && !isPaymentAssigned) return false;
        if (paymentFilter === "unassigned" && isPaymentAssigned) return false;

        // Filter by search term
        if (paymentsSearchword) {
          const term = paymentsSearchword.toLowerCase();
          const matchesSearch =
            (payment.remitter || "").toLowerCase().includes(term) ||
            (payment.description || "").toLowerCase().includes(term) ||
            (payment.iban || "").toLowerCase().includes(term) ||
            String(payment.amount || "").includes(term);
          if (!matchesSearch) return false;
        }

        return true;
      }),
    [payments, paymentFilter, paymentsSearchword]
  );

  useEffect(() => {
    const params = {
      search: claimsSearchword || undefined,
      schadenId: searchParams.get("schadenId") || undefined,
    };

    Object.keys(params).forEach((key) => {
      if (params[key] === undefined) {
        delete params[key];
      }
    });

    const currentParams = Object.fromEntries(searchParams.entries());
    if (JSON.stringify(currentParams) !== JSON.stringify(params)) {
      setSearchParams(params, { replace: true });
    }
  }, [claimsSearchword, setSearchParams]);

  useEffect(() => {
    const schadenId = searchParams.get("schadenId");
    if (schadenId && claims.length > 0) {
      const found = claims.find((c) => String(c.id) === String(schadenId));
      if (found) {
        setSelectedClaim(found);
      }
    }
  }, [claims, searchParams]);

  useEffect(() => {
    fetchAllInitialData();
  }, [id]);

  useEffect(() => {
    if (selectedClaim) {
      fetchBookings(selectedClaim.id);
    }
  }, [selectedClaim?.id]);

  // Debounced search effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setClaimsPage(1);
      setHasMoreClaims(true);
      fetchClaims(1, false);
    }, 300);

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimsSearchword]);

  const handleClaimsScroll = useCallback(() => {
    const container = claimsListRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

    if (isNearBottom && hasMoreClaims && !loadingMoreClaims && !loadingClaims) {
      fetchClaims(claimsPage + 1, true);
    }
  }, [hasMoreClaims, loadingMoreClaims, loadingClaims, claimsPage]);

  useEffect(() => {
    if (!paymentImport) {
      setBreadcrumbs([
        { name: "Home", path: "/home" },
        { name: "Buchungsadministration", path: "/admin/buchungen" },
        { name: "Zahlungsimporte", path: "/admin/buchungen/imports" },
        {
          name: "Zahlungszuordnung",
          path: `/admin/buchungen/imports/${id}/payment-matching`,
        },
      ]);
    } else {
      setBreadcrumbs([
        { name: "Home", path: "/home" },
        { name: "Buchungsadministration", path: "/admin/buchungen" },
        { name: "Zahlungsimporte", path: "/admin/buchungen/imports" },
        {
          name: paymentImport.file_name,
          path: `/admin/buchungen/imports/${id}`,
        },
        {
          name: "Zahlungszuordnung",
          path: `/admin/buchungen/imports/${id}/payment-matching`,
        },
      ]);
    }
  }, [paymentImport, id, setBreadcrumbs]);

  // Lader der Claim die eine Mögliche bzw. Exakte übereinstimmung haben.
  useEffect(() => {
    fetchMatchedClaims(paymentMatchings);
  }, [paymentMatchings]);

  // Füge die Claims mit einer möglichen o. exakten Übereinstimmung in die
  // angezeigte Liste ein. Läuft nach jeder Änderung von `claims` erneut, da
  // ein Seite-1-Fetch (z.B. durch Filter-/Suchänderung) die Liste komplett
  // ersetzt und dabei zuvor gemergte matchedClaims wieder entfernen würde.
  useEffect(() => {
    if (matchedClaims.length === 0) return;

    setClaims((prev) => {
      const existingIds = new Set(prev.map((c) => c.id));
      const filteredNew = matchedClaims.filter((c) => !existingIds.has(c.id));
      if (filteredNew.length === 0) return prev;
      return [...prev, ...filteredNew];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedClaims, claims]);

  const serializeClaim = (rawClaim) => {
    const { formdata, ...rest } = rawClaim;
    const parsedFormdata = JSON.parse(formdata || "{}");
    const {
      schaden_kostenvoranschlag,
      schaden_rechnungsbetrag,
      gesamtschadenkosten,
    } = parsedFormdata;

    return {
      ...rest,
      schaden_kostenvoranschlag,
      schaden_rechnungsbetrag,
      gesamtschadenkosten,
      betrag:
        gesamtschadenkosten ||
        schaden_rechnungsbetrag ||
        schaden_kostenvoranschlag,
      betrag_label: gesamtschadenkosten
        ? "Gesamtschadenkosten"
        : schaden_rechnungsbetrag
          ? "Rechnungsbetrag"
          : "Kostenvoranschlag",
    };
  };

  const fetchClaims = async (page = 1, append = false) => {
    const limit = 8;

    try {
      let options = {
        params: {
          include: ["vertrag", "gesellschaft", "objekt"],
          page,
          limit,
          sort: "-updated_at",
        },
      };

      if (claimsSearchword) {
        options.params.filter = {
          [SEARCH_WORD_KEY]: claimsSearchword,
        };
      }

      await backendGet("data/schadens", {
        label: "Schaden",
        setIsLoading: append ? setLoadingMoreClaims : setLoadingClaims,
        options,
        enqueueSnackbar,
        setResult: (res) => {
          const newClaims = res?.data?.map(serializeClaim) || [];
          const meta = res?.meta;

          if (append) {
            setClaims((prev) => {
              const existingIds = new Set(prev.map((c) => c.id));
              const filteredNew = newClaims.filter(
                (c) => !existingIds.has(c.id)
              );
              return [...prev, ...filteredNew];
            });
          } else {
            setClaims(newClaims);
          }

          setHasMoreClaims(meta?.page < meta?.last_page);
          setClaimsPage(meta?.page || 1);
        },
      });
    } catch (error) {
      enqueueSnackbar("Fehler beim Laden der Schäden.", {
        variant: "error",
      });
    }
  };

  const fetchMatchedClaims = async () => {
    try {
      if (paymentMatchings?.length === 0) return;

      const ids = paymentMatchings.map((pm) => pm.schaden_id);

      let options = {
        params: {
          include: ["vertrag", "gesellschaft", "objekt"],
          filter: {
            id: ids,
          },
        },
      };

      await backendGet("data/schadens", {
        label: "Schaden",
        setIsLoading: setLoadingClaims,
        options,
        enqueueSnackbar,
        setResult: (res) => {
          const newClaims = res?.data?.map(serializeClaim) || [];
          setMatchedClaims((prev) => {
            const existingIds = new Set(prev.map((c) => c.id));
            const filteredNew = newClaims.filter((c) => !existingIds.has(c.id));
            return [...prev, ...filteredNew];
          });
        },
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Fehler beim Laden der Schäden.", {
        variant: "error",
      });
    }
  };

  const fetchDetailedClaim = async (claimId) => {
    if (!claimId) return;

    setLoadingDetailedClaim(true);
    try {
      // Fetch claim and bookings in parallel
      const [claimResponse, bookingsResponse] = await Promise.all([
        backendGet(`data/schadens/${claimId}`, {
          label: "Schadendetails",
          setIsLoading: () => {},
          enqueueSnackbar,
          options: {
            params: {
              include: ["vertrag", "gesellschaft", "objekt"],
            },
          },
        }),
        backendGet(`data/schadens/${claimId}/bookings`, {
          label: "Buchungen",
          setIsLoading: () => {},
          enqueueSnackbar,
          options: {
            params: {
              api_version: "v2",
            },
          },
        }),
      ]);

      const claimData = serializeClaim(claimResponse.data);
      setDetailedClaim(claimData);
      setDetailedClaimBookings(bookingsResponse?.data || []);
    } catch (error) {
      enqueueSnackbar("Fehler beim Laden der Schadendetails.", {
        variant: "error",
      });
    } finally {
      setLoadingDetailedClaim(false);
    }
  };

  const handleOpenClaimDialog = async (claimId) => {
    setIsClaimDialogOpen(true);
    await fetchDetailedClaim(claimId);
  };

  const handleOpenBookingExportDialog = async (bookingExportId) => {
    if (!bookingExportId || !canViewBookingExport) return;

    setIsBookingExportDialogOpen(true);
    setLoadingBookingExport(true);
    try {
      await backendGet(`data/bookingexports/${bookingExportId}`, {
        label: "Buchungsexport",
        setIsLoading: () => {},
        enqueueSnackbar,
        options: {
          params: {
            include: ["payments", "bookings", "schaden", "created_by", "finished_by"],
          },
        },
        setResult: (res) => setDetailedBookingExport(res.data),
      });
    } catch (error) {
      enqueueSnackbar("Fehler beim Laden des Buchungsexports.", {
        variant: "error",
      });
    } finally {
      setLoadingBookingExport(false);
    }
  };

  const handleMenuOpen = (event, bookingId) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuBookingId(bookingId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuBookingId(null);
  };

  const handleOpenDeleteDialog = (booking) => {
    setSelectedBookingToDelete(booking);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteBooking = async () => {
    if (!selectedBookingToDelete) {
      return;
    }

    const success = await deleteBooking(selectedBookingToDelete.id, {
      setIsLoading: setIsDeletingBooking,
    });

    if (success) {
      setIsDeleteDialogOpen(false);
      setSelectedBookingToDelete(null);
    }
  };

  // Schritt 3: alle (exakten/möglichen) Zahlungs-Matches des ausgewählten
  // Schadens - unabhängig davon, welche Zahlung in Schritt 1 den Einstieg
  // gebildet hat (Bestätigungs-Button erlaubt weiterhin die Sammelauswahl
  // mehrerer Matches für diesen Schaden, wie bisher).
  const matchPayments = (claim) => {
    if (!claim) {
      return { exact: [], possible: [], total: 0 };
    }

    const paymentMatches = paymentMatchings.filter(
      (pm) => pm.schaden_id === claim.id
    );

    const exactMatches = paymentMatches.filter((m) => m.status === "exact");
    const possibleMatches = paymentMatches.filter(
      (m) => m.status === "possible"
    );

    return {
      exact: exactMatches,
      possible: possibleMatches,
      total: exactMatches.length + possibleMatches.length,
    };
  };

  // Schritt 2: welche Schäden passen (exakt/möglich) zur aktuell in Schritt 1
  // ausgewählten Zahlung? Analog zu matchPayments, nur nach payment_id statt
  // schaden_id gefiltert (clientseitige Umkehrung der Auswahlrichtung).
  const matchClaimsToSelectedPayment = (claim) => {
    if (!claim || !selectedPayment) {
      return { exact: [], possible: [], total: 0 };
    }

    const paymentMatches = paymentMatchings.filter(
      (pm) =>
        pm.schaden_id === claim.id && pm.payment_id === selectedPayment.id
    );

    const exactMatches = paymentMatches.filter((m) => m.status === "exact");
    const possibleMatches = paymentMatches.filter(
      (m) => m.status === "possible"
    );

    return {
      exact: exactMatches,
      possible: possibleMatches,
      total: exactMatches.length + possibleMatches.length,
    };
  };

  const getMatchFilterStatus = (claim) => {
    const matches = matchClaimsToSelectedPayment(claim);
    if (matches.exact.length > 0) return "exact";
    if (matches.possible.length > 0) return "possible";
    return "none";
  };

  // Schritt 1: wie viele exakte/mögliche Matches hat eine Zahlung insgesamt
  // (über alle Schäden hinweg) - für die Badges in der Zahlungsliste.
  const getPaymentMatchCounts = (paymentId) => {
    const matches = paymentMatchings.filter((pm) => pm.payment_id === paymentId);
    return {
      exact: matches.filter((m) => m.status === "exact").length,
      possible: matches.filter((m) => m.status === "possible").length,
    };
  };

  const filteredClaims = useMemo(
    () =>
      claims.filter((c) =>
        // Solange nicht gesucht wird, zeigt Schritt 2 nur zur Zahlung
        // passende Schäden (exakt oder möglich). Erst eine aktive Suche
        // ersetzt diese Liste durch das ungefilterte Backend-Suchergebnis.
        claimsSearchword ? true : matchClaimsToSelectedPayment(c)?.total > 0
      ),
    [claims, paymentMatchings, selectedPayment, claimsSearchword]
  );

  const sortedFilteredClaims = useMemo(() => {
    const order = { exact: 0, possible: 1, none: 2 };

    return [...filteredClaims].sort((a, b) => {
      const statusA = getMatchFilterStatus(a);
      const statusB = getMatchFilterStatus(b);
      return order[statusA] - order[statusB];
    });
  }, [filteredClaims, paymentMatchings, selectedPayment]);

  // Schritt 1: Zahlung als Ausgangspunkt auswählen. Setzt eine ggf. bereits
  // getroffene Schaden-/Zahlungsauswahl zurück, da diese sich auf eine andere
  // Zahlung bezogen hätte.
  const handleSelectPayment = (payment) => {
    setSelectedPayment(payment);
    setSelectedClaim(null);
    setSelectedPayments([]);

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("schadenId");
      return params;
    });
  };

  // Zurück-Navigation Schritt 2 -> Schritt 1: andere Zahlung wählen.
  const handleBackToPayments = () => {
    setSelectedPayment(null);
    setSelectedClaim(null);
    setSelectedPayments([]);

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("schadenId");
      return params;
    });
  };

  // Zurück-Navigation Schritt 3 -> Schritt 2: anderen Schaden wählen,
  // ausgewählte Zahlung (Schritt 1) bleibt erhalten.
  const handleBackToClaims = () => {
    setSelectedClaim(null);
    setSelectedPayments([]);

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("schadenId");
      return params;
    });
  };

  const togglePaymentSelection = (paymentId) => {
    setSelectedPayments((prev) =>
      prev.includes(paymentId)
        ? prev.filter((id) => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  const getSelectedPaymentsTotal = () => {
    return selectedPayments.reduce((sum, paymentId) => {
      const payment =
        payments.find((p) => p.id === paymentId) ||
        paymentMatchings.find((pm) => pm.payment_id === paymentId)?.payment;
      return sum + (payment ? parseFloat(payment.amount || 0) : 0);
    }, 0);
  };

  // Ist die in Schritt 1 gewählte Zahlung ein exaktes Matching zum
  // ausgewählten Schaden? Steuert Button-Farbe und ob vor der Zuordnung erst
  // eine Sicherheitsabfrage (ConfirmNonExactMatchDialog) nötig ist.
  const isAnchorPaymentExactMatch = Boolean(
    selectedClaim &&
      selectedPayment &&
      paymentMatchings.some(
        (pm) =>
          pm.schaden_id === selectedClaim.id &&
          pm.payment_id === selectedPayment.id &&
          pm.status === "exact"
      )
  );

  const proceedWithOverpaymentCheck = async () => {
    const selectedTotal = getSelectedPaymentsTotal();
    const claimAmount = parseFloat(
      selectedClaim.auszahlungsbetrag ||
        selectedClaim.gesamtschadenkosten ||
        selectedClaim.schaden_rechnungsbetrag ||
        selectedClaim.schaden_kostenvoranschlag ||
        0
    );

    const existingInSum = bookingIn.reduce(
      (sum, b) => sum + parseFloat(b.amount || 0),
      0
    );

    const totalIn = existingInSum + selectedTotal;
    const overpayment = claimAmount > 0 ? totalIn - claimAmount : 0;

    if (overpayment > 0.01) {
      setOverpaymentAmount(overpayment);
      setIsOverpaymentDialogOpen(true);
      return;
    }

    await doAssignPayments(false);
  };

  const confirmSelectedPayments = async () => {
    if (!selectedClaim || selectedPayments.length === 0) return;

    if (!isAnchorPaymentExactMatch) {
      setIsNonExactMatchDialogOpen(true);
      return;
    }

    await proceedWithOverpaymentCheck();
  };

  const handleConfirmNonExactMatch = async () => {
    setIsNonExactMatchDialogOpen(false);
    await proceedWithOverpaymentCheck();
  };

  const doAssignPayments = async (createRefund = false) => {
    if (!selectedClaim || selectedPayments.length === 0) return;

    await assignPaymentsToClaim(selectedClaim.id, selectedPayments, {
      createRefund,
    });

    setSelectedPayments([]);
    setIsOverpaymentDialogOpen(false);
    setOverpaymentAmount(0);

    if (selectedClaim) {
      fetchBookings(selectedClaim.id);
    }
  };

  // Sammelaktion ist unabhängig von Zahlungs-/Schaden-Auswahl (Schritt 1-3)
  // und bezieht sich auf alle noch nicht zugeordneten exakten Matches des
  // gesamten Payment-Imports (paymentMatchings enthält bereits nur
  // is_assigned=false-Einträge, siehe usePaymentMatchingData.fetchPaymentMatchings).
  const hasUnassignedExactMatches = paymentMatchings.some(
    (pm) => pm.status === "exact"
  );

  const handleApproveAllExactMatches = async () => {
    if (!hasUnassignedExactMatches) return;

    await approveAllExactMatches({
      setIsLoading: setIsAssigning,
      onSuccess: () => {
        if (selectedClaim) {
          fetchBookings(selectedClaim.id);
        }
      },
    });
  };

  const remainingPayments = payments.filter(
    (payment) => payment.status === "imported"
  );
  const hasRemainingMatches = remainingPayments.some((payment) =>
    paymentMatchings.some((pm) => pm.payment_id === payment.id)
  );

  const handleIgnorePayment = async (paymentId) => {
    if (selectedPayment?.id === paymentId) {
      handleBackToPayments();
    }
    await ignorePayment(paymentId);
  };

  const handleIgnoreRemaining = async () => {
    if (selectedPayment) {
      handleBackToPayments();
    }
    await ignoreRemainingPayments();
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h1" gutterBottom>
          Zahlungszuordnung
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          height: "80vh",
        }}
      >
        {/* Schritt 1: Zahlungen */}
        <Box
          sx={{
            flex: "0 0 36%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <PaymentMatchingPaymentsPanel
            paymentImport={paymentImport}
            paymentsSearchword={paymentsSearchword}
            onPaymentsSearchwordChange={setPaymentsSearchword}
            paymentFilter={paymentFilter}
            onPaymentFilterChange={setPaymentFilter}
            filteredPayments={filteredPayments}
            loadingPayments={loadingPayments}
            selectedPayment={selectedPayment}
            onSelectPayment={handleSelectPayment}
            onAssignPaymentToExport={setPaymentToAssignToExport}
            onOpenBookingExport={handleOpenBookingExportDialog}
            canViewBookingExport={canViewBookingExport}
            getPaymentMatchCounts={getPaymentMatchCounts}
            handleOpenClaimDialog={handleOpenClaimDialog}
            approveAllExactMatches={handleApproveAllExactMatches}
            hasUnassignedExactMatches={hasUnassignedExactMatches}
            isAssigning={isAssigning}
            onIgnorePayment={handleIgnorePayment}
            onIgnoreRemaining={handleIgnoreRemaining}
            remainingCount={remainingPayments.length}
            hasRemainingMatches={hasRemainingMatches}
          />
        </Box>

        {/* Schritt 2: mögliche Schäden zur ausgewählten Zahlung */}
        <Box
          sx={{
            flex: "0 0 32%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <PaymentMatchingClaimList
            claimsSearchword={claimsSearchword}
            onClaimsSearchwordChange={setclaimsSearchword}
            claimsListRef={claimsListRef}
            onClaimsScroll={handleClaimsScroll}
            loadingClaims={loadingClaims}
            sortedFilteredClaims={sortedFilteredClaims}
            matchPayments={matchClaimsToSelectedPayment}
            selectedPayment={selectedPayment}
            selectedClaim={selectedClaim}
            onSelectClaim={(claim) => {
              setSelectedClaim(claim);

              const exactPayments = paymentMatchings
                .filter(
                  (pm) => pm.schaden_id === claim.id && pm.status === "exact"
                )
                .map((pm) => pm.payment_id);

              const preSelected = new Set(exactPayments);
              if (selectedPayment) {
                preSelected.add(selectedPayment.id);
              }

              setSelectedPayments([...preSelected]);

              setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set("schadenId", claim.id);
                return params;
              });
            }}
            onBackToPayments={handleBackToPayments}
            loadingMoreClaims={loadingMoreClaims}
            hasMoreClaims={hasMoreClaims}
          />
        </Box>

        {/* Schritt 3: Schadenansicht mit Zuordnungs-Bestätigung */}
        <Box
          sx={{
            flex: "0 0 32%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <PaymentMatchingClaimDetailsPanel
            selectedClaim={selectedClaim}
            selectedPayment={selectedPayment}
            bookings={bookings}
            bookingIn={bookingIn}
            bookingOut={bookingOut}
            differenceAmount={differenceAmount}
            matchPayments={matchPayments}
            selectedPayments={selectedPayments}
            togglePaymentSelection={togglePaymentSelection}
            confirmSelectedPayments={confirmSelectedPayments}
            isAnchorPaymentExactMatch={isAnchorPaymentExactMatch}
            loadingMatchings={loadingMatchings}
            handleOpenClaimDialog={handleOpenClaimDialog}
            menuAnchorEl={menuAnchorEl}
            menuBookingId={menuBookingId}
            handleMenuOpen={handleMenuOpen}
            handleMenuClose={handleMenuClose}
            handleOpenDeleteDialog={handleOpenDeleteDialog}
            onBack={handleBackToClaims}
          />
        </Box>
      </Box>

      <DeleteBookingModal
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteBooking}
        isLoading={isDeletingBooking}
        bookingId={selectedBookingToDelete}
      />

      <ClaimInfoModal
        open={isClaimDialogOpen}
        onClose={() => setIsClaimDialogOpen(false)}
        claim={detailedClaim}
        claimBookings={detailedClaimBookings}
        isLoading={loadingDetailedClaim}
      />

      <BookingExportInfoModal
        open={isBookingExportDialogOpen}
        onClose={() => setIsBookingExportDialogOpen(false)}
        bookingExport={detailedBookingExport}
        isLoading={loadingBookingExport}
      />

      {paymentToAssignToExport && (
        <AssignExportModal
          open
          onClose={() => setPaymentToAssignToExport(null)}
          payment={paymentToAssignToExport}
          onActionComplete={fetchPayments}
        />
      )}

      <OverpaymentDialog
        open={isOverpaymentDialogOpen}
        onClose={() => setIsOverpaymentDialogOpen(false)}
        overpaymentAmount={overpaymentAmount}
        onAssignWithRefund={() => doAssignPayments(true)}
        onAssignWithoutRefund={() => doAssignPayments(false)}
      />

      <ConfirmNonExactMatchDialog
        open={isNonExactMatchDialogOpen}
        onClose={() => setIsNonExactMatchDialogOpen(false)}
        onConfirm={handleConfirmNonExactMatch}
      />
    </Box>
  );
}
